// CardDiagFormQuestion.tsx
import React from "react";
import { Input } from "semantic-ui-react";
import { makeStyles } from "@mui/styles";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";

import htmlParse from "html-react-parser";

import CardRadioButton from "./CardRadioButton";

type CardDiagFormQuestionType = {
  extraItem?: {
    label: string;
    type: "Text" | "Integer" | "Select" | "MultiSelect" | "Description";
    value: string | string[];
    nullOk: boolean;
    options?: string[];
    tag?: string;
  };
  choicesData?: {
    label: string;
    options: string[];
    selectedValue: string | null;
  };
  itemIndex: number;
  hideQuestionNumber?: boolean;
  showOnlySelected?: boolean;
  onExtraItemChange?: (itemIndex: number, value: string) => void;
  onChoiceSelect?: (value: string) => void;
};

const COLORS = {
  grey: "#3C3C3C",
  pink: "var(--primary-theme-color)",
  red: "#FF5252",
  textGrey: "#666666",
};

const useStyles = makeStyles(() => ({
  card: {
    "&.MuiPaper-root": {
      marginBottom: "16px",
      borderRadius: "12px",
      border: "1px solid rgba(217, 217, 217, 1)",
      boxShadow: "none",
      overflow: "unset",
    },
  },
  questionNumber: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    backgroundColor: "rgba(255, 229, 229, 1)",
    color: COLORS.pink,
    fontSize: "14px",
    fontWeight: "600",
    marginRight: "8px",
  },
  questionTitle: {
    fontSize: "15px",
    color: COLORS.grey,
    marginBottom: "4px",
    lineHeight: "1.5",
  },
  required: {
    "&::after": {
      content: '"*"',
      color: COLORS.red,
      marginLeft: "2px",
    },
  },
  descriptionText: {
    fontSize: "15px",
    color: COLORS.grey,
    marginBottom: "16px",
    lineHeight: "1.5",
  },
  radioGroup: {
    "& .radio-check-button.MuiRadio-root:not(.Mui-checked)": {
      color: "rgba(117, 117, 117, 1)",
    },
    "& .MuiPaper-root": {
      boxShadow: "none",
      borderRadius: "6px",
      marginBottom: "0.5rem",
      backgroundColor: "rgba(243, 243, 243, 1)",
      border: "none",
      "& .MuiRadio-root": { transform: "scale(1)", margin: "0 -3px" },
      "& .MuiCardContent-root": { padding: "12.5px 18px !important" },
      "& .radio-check-label": { fontSize: "14px" },
    },
  },
  textInput: {
    "& .ui.input > input": { backgroundColor: "rgba(245, 245, 245, 1)" },
  },
}));

const CardDiagFormQuestion = (props: CardDiagFormQuestionType) => {
  const classes = useStyles();
  const { extraItem, choicesData, itemIndex } = props;

  // Handle choices type
  if (choicesData) {
    return (
      <Card key={itemIndex} className={classes.card}>
        <CardContent>
          {choicesData.label && (
            <div className={classes.questionTitle}>{htmlParse(choicesData.label || "")}</div>
          )}
          <div className={classes.radioGroup}>
            {choicesData.options.map((option) => (
              <CardRadioButton
                key={option}
                label={option}
                checked={choicesData.selectedValue === option}
                radioPosition="left"
                onClick={() => props.onChoiceSelect?.(option)}
              />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!extraItem) {
    return null;
  }

  // ถ้าเป็น Description ที่ nullOk: true แสดงแค่ข้อความ (ไม่มี Card, ไม่มี input)
  if (extraItem.type === "Description" && extraItem.nullOk) {
    return <div className={classes.descriptionText}>{htmlParse(extraItem.label || "")}</div>;
  }

  // ที่เหลือทั้งหมดแสดงเป็น Card พร้อม input
  const isRequired = !extraItem.nullOk;
  const questionType = extraItem.type === "Select" ? "radio" : "text";
  const currentValue = Array.isArray(extraItem.value) ? extraItem.value[0] : extraItem.value;

  return (
    <Card key={itemIndex} className={classes.card}>
      <CardContent>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: props.hideQuestionNumber ? "1fr" : "32px 1fr",
            gap: "8px",
            alignItems: "start",
          }}
        >
          {/* Question Number */}
          {!props.hideQuestionNumber && (
            <span className={classes.questionNumber}>{itemIndex + 1}</span>
          )}

          {/* Question Content */}
          <div>
            <div className={`${classes.questionTitle} ${isRequired ? classes.required : ""}`}>
              {htmlParse(extraItem.label || "")}
            </div>

            {/* Radio Options for Select type */}
            {questionType === "radio" && extraItem.options && (
              <div className={classes.radioGroup}>
                {extraItem.options
                  .filter((option) => (props.showOnlySelected ? currentValue === option : true))
                  .map((option, index) => (
                    <CardRadioButton
                      key={`${option}-${itemIndex}-${index}`}
                      label={option.trim()}
                      checked={currentValue === option}
                      radioPosition="left"
                      onClick={() => props.onExtraItemChange?.(itemIndex, option)}
                    />
                  ))}
              </div>
            )}

            {/* Text Input for Text/Integer type */}
            {questionType === "text" && (
              <div className={classes.textInput}>
                <Input
                  className="provider-input"
                  fluid
                  placeholder={extraItem.type === "Integer" ? "กรุณากรอกตัวเลข" : "กรุณาตอบคำถาม"}
                  value={currentValue || ""}
                  onChange={(e) => props.onExtraItemChange?.(itemIndex, e.target.value)}
                />
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CardDiagFormQuestion;
